'use client'

import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  pageSize?: number
  pageIndex?: number
  rowCount?: number
  isLoading?: boolean
  onPageChange?: (pageIndex: number) => void
  onRowClick?: (row: TData) => void
}

export function DataTable<TData, TValue>({
  columns,
  data,
  pageSize = 10,
  pageIndex,
  rowCount,
  isLoading,
  onPageChange,
  onRowClick,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: (updater) => {
      if (typeof updater === 'function' && onPageChange) {
        const nextState = updater(table.getState().pagination)
        onPageChange(nextState.pageIndex)
      }
    },
    state: {
      pagination: {
        pageIndex: pageIndex ?? 0,
        pageSize: onPageChange ? pageSize : data.length || 100000,
      },
    },
    manualPagination: rowCount !== undefined,
    rowCount: rowCount,
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const lastPageIndex = useRef(table.getState().pagination.pageIndex)

  useEffect(() => {
    const currentPageIndex = table.getState().pagination.pageIndex
    if (lastPageIndex.current !== currentPageIndex) {
      lastPageIndex.current = currentPageIndex
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [table.getState().pagination.pageIndex])

  return (
    <div
      ref={containerRef}
      className="space-y-4"
    >
      <div className="overflow-hidden rounded-md border bg-white shadow-xs">
        <Table>
          <TableHeader className="bg-gray-50/50">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className="text-xs font-bold tracking-wider text-gray-500 uppercase"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(pageSize)].map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <div className="bg-muted h-6 w-full animate-pulse rounded-md" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(
                    'transition-colors hover:bg-gray-50/50',
                    onRowClick && 'cursor-pointer',
                  )}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="py-4"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  ไม่พบข้อมูล
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {onPageChange && (
        <div className="flex items-center justify-between px-2">
          <div className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
            หน้า{' '}
            <span className="text-foreground font-bold">
              {table.getState().pagination.pageIndex + 1}
            </span>{' '}
            จาก{' '}
            <span className="text-foreground font-bold">
              {table.getPageCount()}
            </span>
          </div>
          <div className="flex items-center gap-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="h-9 gap-1 rounded-lg px-3 font-semibold transition-all active:scale-95"
            >
              <ChevronLeft className="size-4" />
              ก่อนหน้า
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="h-9 gap-1 rounded-lg px-3 font-semibold transition-all active:scale-95"
            >
              ถัดไป
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
